import { LoggableError as BaseLoggableError } from '@yandex-int/messenger.errors';

import { LogErrorOptions, SafeLogErrorOptions } from '../log-error';
import { isError } from './isError';

type TelemostLoggableError = BaseLoggableError<LogErrorOptions>;

export const LoggableError = BaseLoggableError as unknown as TelemostLoggableError & {
    new (message: string, loggerFields?: LogErrorOptions, stack?: string): TelemostLoggableError;
    is<E extends Error, Args extends unknown[]>(this: new (...args: Args) => E, error: unknown): error is E;
};

export const isLoggableError = (error: Error): error is typeof LoggableError => {
    return LoggableError.is(error);
};

export function mergeLogOptions(
    sourceFields: SafeLogErrorOptions,
    extraFields: Maybe<LogErrorOptions>,
    defaultAdditional?: LogErrorOptions['additional']
): SafeLogErrorOptions;
export function mergeLogOptions(
    sourceFields: Maybe<LogErrorOptions>,
    extraFields: SafeLogErrorOptions,
    defaultAdditional?: LogErrorOptions['additional']
): SafeLogErrorOptions;
export function mergeLogOptions(
    sourceFields: Maybe<LogErrorOptions>,
    extraFields: Maybe<LogErrorOptions>,
    defaultAdditional?: LogErrorOptions['additional']
): LogErrorOptions;
export function mergeLogOptions(
    sourceFields: Maybe<LogErrorOptions>,
    extraFields: Maybe<LogErrorOptions>,
    defaultAdditional?: LogErrorOptions['additional']
): LogErrorOptions {
    if (!extraFields && !defaultAdditional) {
        return sourceFields || {};
    }

    if (!sourceFields && !defaultAdditional) {
        return extraFields || {};
    }

    return {
        ...sourceFields,
        ...extraFields,
        additional: {
            ...defaultAdditional,
            ...sourceFields?.additional,
            ...extraFields?.additional,
            ...(sourceFields?.method
                ? {
                      sourceMethod: sourceFields.method,
                  }
                : undefined),
            ...(sourceFields?.additional?.kind
                ? {
                      sourceKind: sourceFields.additional.kind,
                  }
                : undefined),
        },
    };
}

export const toLoggableError = (error: unknown, options?: LogErrorOptions) => {
    if (typeof error === 'string') {
        return new LoggableError(error, options);
    }

    if (!isError(error)) {
        if (typeof error === 'object' && !Array.isArray(error) && error !== null) {
            return new LoggableError('Unknown Error', mergeLogOptions({ additional: { error } }, options));
        }
        return new LoggableError('Unknown Error', options);
    }

    if (isLoggableError(error)) {
        if (options) {
            error.loggerFields = mergeLogOptions(error.loggerFields, options);
        }

        return error;
    }

    return new LoggableError(error.message, options, error.stack);
};
